import { Button, HelperText, Label, TextInput } from 'flowbite-react';
import { type FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SecretBundlePanel } from '../../components/clients/SecretBundlePanel.js';
import { useClientQuery } from '../../queries/use-client.js';
import { useUpdateClientMutation } from '../../queries/use-update-client.js';

const CLIENT_BUNDLE_FIELDS = [
  {
    key: 'gitHubToken',
    label: 'GitHub PAT (per-client)',
    hint: 'Personal access token used by the github-backup pipeline. Per-license overrides live on each License bundle.',
    kind: 'string' as const,
  },
];

/**
 * `/clients/{id}/github-opts` — edits the three GitHub-backup fields
 * stored on the `clients` row (repo owner, repo name, branch) plus
 * the per-tenant `gitHubToken` Secrets Manager bundle field through
 * `<SecretBundlePanel>`. The token landed when the operator-facing
 * secret-storage surface shipped (2026-05).
 */
export function GitHubOptions(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data, error, isLoading, isError } = useClientQuery(id);

  const [repoName, setRepoName] = useState('');
  const [repoOwner, setRepoOwner] = useState('');
  const [branch, setBranch] = useState('');

  const update = useUpdateClientMutation();

  useEffect(() => {
    if (data) {
      setRepoName(data.githubRepoName ?? '');
      setRepoOwner(data.githubRepoOwner ?? '');
      setBranch(data.githubBranch ?? '');
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  if (isLoading) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400" role="status">
        Loading client…
      </p>
    );
  }
  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
      >
        {error instanceof Error ? error.message : 'Failed to load client'}
      </div>
    );
  }
  if (!data || !id) return <></>;

  const dirty =
    nullIfBlank(repoName) !== data.githubRepoName ||
    nullIfBlank(repoOwner) !== data.githubRepoOwner ||
    nullIfBlank(branch) !== data.githubBranch;
  const canSubmit = dirty && !update.isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    update.mutate({
      id,
      patch: {
        githubRepoName: nullIfBlank(repoName),
        githubRepoOwner: nullIfBlank(repoOwner),
        githubBranch: nullIfBlank(branch),
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      aria-label="Edit GitHub options"
      className="max-w-2xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="space-y-5">
        <div>
          <Label htmlFor="gh-repo-owner">Repository owner</Label>
          <TextInput
            id="gh-repo-owner"
            type="text"
            value={repoOwner}
            onChange={(e) => setRepoOwner(e.target.value)}
            placeholder="org or user (e.g. workfocusio)"
            disabled={update.isPending}
          />
        </div>
        <div>
          <Label htmlFor="gh-repo-name">Repository name</Label>
          <TextInput
            id="gh-repo-name"
            type="text"
            value={repoName}
            onChange={(e) => setRepoName(e.target.value)}
            placeholder="e.g. tenant-config"
            disabled={update.isPending}
          />
        </div>
        <div>
          <Label htmlFor="gh-branch">Branch</Label>
          <TextInput
            id="gh-branch"
            type="text"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            placeholder="main"
            disabled={update.isPending}
          />
          <HelperText className="mt-1">Default branch the backup engine commits to.</HelperText>
        </div>

        {update.isError ? (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {update.error instanceof Error ? update.error.message : 'Failed to save changes'}
          </div>
        ) : null}
        {update.isSuccess ? (
          <div
            role="status"
            className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-900 dark:border-green-700 dark:bg-green-950 dark:text-green-200"
          >
            Changes saved.
          </div>
        ) : null}

        <div className="flex items-center justify-end border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button type="submit" disabled={!canSubmit}>
            {update.isPending ? 'Saving…' : 'Update'}
          </Button>
        </div>
      </div>

      <div className="mt-6">
        <SecretBundlePanel
          bundlePath={`/v1/clients/${id}`}
          fields={CLIENT_BUNDLE_FIELDS}
          title="GitHub credentials"
          subtitle="Per-tenant Secrets Manager bundle. Used by github-backup unless a license overrides."
        />
      </div>
    </form>
  );
}

function nullIfBlank(s: string): string | null {
  return s.trim() === '' ? null : s.trim();
}
